'use client';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Calendar } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/atoms/carousel';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';

// Função para remover estilos inline do HTML
const stripInlineStyles = (html: string): string => {
  if (!html) return '';

  // Remove atributos style dos elementos
  return html.replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '');
};

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  category?: string;
  slug?: string;
}

interface NewsSectionProps {
  title?: string;
  subtitle?: string;
  news: NewsItem[];
  viewAllText?: string;
  className?: string;
}

interface NewsCardProps {
  news: NewsItem;
  isExpanded: boolean;
  onHover: () => void;
}

const NewsCard: React.FC<NewsCardProps> = ({ news, isExpanded, onHover }) => {
  const router = useRouter();
  // Remove estilos inline do excerpt
  const cleanExcerpt = useMemo(() => stripInlineStyles(news.excerpt), [news.excerpt]);

  const handleClick = () => {
    if (news.slug) {
      router.push(`/noticias-eventos/noticias/${news.slug}`);
    }
  };

  return (
    <article
      onMouseEnter={onHover}
      className={`relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 ease-in-out shadow-[0px_8px_20px_0px_rgba(139,141,142,0.15)] min-h-[400px] md:min-h-[500px] lg:min-h-[576px] ${isExpanded ? 'flex-[2.5]' : 'flex-1'
        }`}
    >
      {/* Imagem de fundo */}
      <div className="absolute inset-0">
        <Image
          src={news.image || '/event.png'}
          alt={news.title}
          fill
          className="object-cover"
          priority
        />
      </div>

      {/* Overlay gradiente */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/70 via-black/40 to-black/90 rounded-3xl" />

      {/* Conteúdo */}
      <div className="relative h-full p-8 flex flex-col justify-end gap-4">
        {/* Data */}
        <div className="flex items-center gap-2 z-10">
          <Calendar className="w-5 h-5 text-[#FFEF72]" strokeWidth={2} />
          <time className="text-white text-lg font-medium font-poppins uppercase tracking-wide">
            {news.date}
          </time>
        </div>

        {/* Título */}
        <h3
          className={`text-white font-semibold font-poppins leading-7 md:leading-8 line-clamp-4 z-10 transition-all duration-300 text-lg sm:text-xl md:text-2xl ${isExpanded ? 'max-w-full' : 'max-w-full md:max-w-[280px]'
            }`}
        >
          {news.title}
        </h3>

        {/* Descrição - visível apenas quando expandido */}
        <div className={`transition-all duration-500 overflow-hidden ${isExpanded ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="text-gray-100 text-sm md:text-base font-light font-poppins line-clamp-4 md:line-clamp-5 leading-5 md:leading-6" dangerouslySetInnerHTML={{ __html: cleanExcerpt }} />
        </div>

        {/* Botão de ação */}
        <div
          className={`z-10 transition-all duration-300 ${isExpanded ? 'self-end' : 'absolute bottom-8 right-8'
            }`}
        >
          <button
            onClick={handleClick}
            className="cursor-pointer w-14 h-14 bg-[#F05A49] rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:bg-[#d94839] hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#F05A49] focus:ring-offset-2"
            aria-label="Ver mais detalhes"
          >
            <span className="text-white text-3xl font-normal font-rubik leading-[49.50px] tracking-wide">
              +
            </span>
          </button>
        </div>
      </div>
    </article>
  );
};

export const NewsSection: React.FC<NewsSectionProps> = ({
  title = 'Últimas Notícias',
  subtitle = 'Notícias',
  news,
  viewAllText = 'Ver todas as notícias',
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedCardIndex, setExpandedCardIndex] = useState(0); // Primeiro card expandido por padrão
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(0);
  const [carouselCount, setCarouselCount] = useState(0);
  const itemsPerPage = 3; // Mostra 3 cards por vez
  const totalPages = Math.ceil(news.length / itemsPerPage);

  // Carousel API handlers
  useEffect(() => {
    if (!carouselApi) return;

    setCarouselCount(carouselApi.scrollSnapList().length);
    setCarouselCurrentIndex(carouselApi.selectedScrollSnap());

    carouselApi.on('select', () => {
      setCarouselCurrentIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const scrollToIndex = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setExpandedCardIndex(0); // Reset para primeiro card expandido
  };

  const visibleNews = news.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <section className={`w-full py-16 px-4 bg-white mt-22 ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 ">
          <div>
            <div className="mb-6 lg:max-w-sm">
              <HighlightedHeading text={title || ''} positions={[0]} />
            </div>
            <div className="inline-block bg-stone-300/20 rounded-[30px] px-4 py-2">
              <span className="text-gray-700 text-base font-normal font-poppins">
                {subtitle}
              </span>
            </div>
          </div>

          {/* Botão Ver Todas - Desktop */}
          <button className="hidden md:flex cursor-pointer mt-6 md:mt-0 bg-[#F05A49] hover:bg-[#d94839] rounded-[30px] px-8 py-3 items-center gap-2 transition-colors shadow-md" onClick={() => window.location.href = '/noticias-eventos'}>
            <span className="text-white text-base font-medium font-poppins">
              {viewAllText}
            </span>
          </button>
        </div>

        {/* Container do Carousel */}
        <div className="relative">
          {/* Desktop: Grid com paginação (apenas xl+) */}
          <div className="hidden xl:block">
            <div className="flex gap-6 w-full">
              {visibleNews.map((item, index) => (
                <NewsCard
                  key={item.id || `news-desktop-${index}`}
                  news={item}
                  isExpanded={expandedCardIndex === index}
                  onHover={() => setExpandedCardIndex(index)}
                />
              ))}
            </div>
            {/* Indicadores de Paginação - Desktop */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-3 mt-12">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={`news-pagination-${index}`}
                    onClick={() => goToSlide(index)}
                    className={`cursor-pointer rounded-full transition-all ${index === currentIndex
                        ? 'w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                      }`}
                    aria-label={`Ir para slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mobile e Tablet: Carrossel */}
          <div className="xl:hidden">
            <Carousel className="w-full" setApi={setCarouselApi}>
              <CarouselContent className="-ml-2 md:-ml-4">
                {news.map((item, index) => (
                  <CarouselItem key={item.id || `news-carousel-${index}`} className="pl-2 md:pl-4 basis-[90%] sm:basis-[80%] md:basis-[65%] lg:basis-[50%]">
                    <NewsCard
                      news={item}
                      isExpanded={true}
                      onHover={() => { }}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
            {/* Pagination Dots - Mobile e Tablet */}
            {carouselCount > 1 && (
              <div className="flex justify-center gap-2 sm:gap-3 mt-6">
                {Array.from({ length: carouselCount }).map((_, index) => (
                  <button
                    key={`news-carousel-dot-${index}`}
                    onClick={() => scrollToIndex(index)}
                    className={`cursor-pointer rounded-full transition-all ${index === carouselCurrentIndex
                        ? 'w-10 sm:w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                      }`}
                    aria-label={`Ir para slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
            {/* Botão Ver Todas - Mobile */}
            <div className="flex justify-center mt-8">
              <button className="cursor-pointer bg-[#F05A49] hover:bg-[#d94839] rounded-[30px] px-8 py-3 flex items-center gap-2 transition-colors shadow-md" onClick={() => window.location.href = '/noticias-eventos'}>
                <span className="text-white text-base font-medium font-poppins">
                  {viewAllText}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};